import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, RefreshCw, Loader as Loader2, Package, DollarSign, Clock, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Circle as XCircle, X, ChevronDown, ChevronUp, Truck, Zap, MapPin, TrendingUp, Calendar, Filter, ArrowUpRight, PackageCheck, Timer, ChartBar as BarChart3, Wifi, WifiOff, ClipboardList, History, TriangleAlert as AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { supabase } from '../lib/supabase';
import type { ShopifyOrder, OrderStats, FinishOrderResponse, ReprintLabelResponse } from '../types/shopify';
import { formatCurrency } from '../types/shopify';
import OrdersList from '../components/shopify/OrdersList';
import OrderDetails from '../components/shopify/OrderDetails';
import { OrderInvoice } from '../components/shopify/OrderInvoice';
import OrderFilters from '../components/shopify/OrderFilters';
import FulfillmentModal from '../components/shopify/FulfillmentModal';
import FinishOrderModal from '../components/shopify/FinishOrderModal';
import BulkPickListPrintable from '../components/shopify/BulkPickListPrintable';
import BulkOrderActionsModal from '../components/shopify/BulkOrderActionsModal';
import PartialRefundWarningModal from '../components/shopify/PartialRefundWarningModal';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { useOrderStats, formatStatNumber } from '../hooks/useOrderStats';
import { refreshToken, isTokenExpired, getValidAuthHeaders } from '../lib/auth';
import { getAdminId } from '../utils/getAdminId';

interface FulfillmentSettings {
  auto_fulfillment_enabled: boolean;
  default_carrier: string;
  default_shopify_location_id: number | null;
}

interface ExtendedStats extends OrderStats {
  withTracking: number;
  printedToday: number;
  readyToShip: number;
  toPrepare: number;
  avgOrderValue: number;
  todayOrders: number;
  todayRevenue: number;
  ordersWithMissingItems: number;
}

interface ShopifyLocation {
  id: number;
  name: string;
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface SyncProgress {
  isActive: boolean;
  syncedCount: number;
  totalToSync: number;
  status: string;
  jobId: string | null;
  canCancel: boolean;
}

const CARRIERS = [
  { value: 'PostNL', label: 'PostNL' },
  { value: 'DHL', label: 'DHL' },
  { value: 'DPD', label: 'DPD' },
  { value: 'UPS', label: 'UPS' },
  { value: 'FedEx', label: 'FedEx' },
  { value: 'GLS', label: 'GLS' },
  { value: 'Budbee', label: 'Budbee' },
  { value: 'Other', label: 'Other' },
];

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subValue?: string;
  trend?: { value: number; positive: boolean };
  gradient: string;
  onClick?: () => void;
}> = ({ icon, label, value, subValue, trend, gradient, onClick }) => (
  <div
    className={`relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-5 transition-all duration-200 ${
      onClick ? 'cursor-pointer hover:shadow-lg hover:scale-[1.02]' : ''
    }`}
    onClick={onClick}
  >
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</p>
        <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
        {subValue && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{subValue}</p>
        )}
        {trend && (
          <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${
            trend.positive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
          }`}>
            <ArrowUpRight className={`h-3 w-3 ${!trend.positive ? 'rotate-90' : ''}`} />
            {trend.value}% vs last week
          </div>
        )}
      </div>
      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
        {icon}
      </div>
    </div>
    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`} />
  </div>
);

const ProgressRing: React.FC<{ percentage: number; size?: number; strokeWidth?: number }> = ({
  percentage,
  size = 60,
  strokeWidth = 6,
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (percentage / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg className="transform -rotate-90" width={size} height={size}>
        <circle
          className="text-gray-200 dark:text-gray-700"
          strokeWidth={strokeWidth}
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          className="text-emerald-500 transition-all duration-500 ease-out"
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          stroke="currentColor"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
          {Math.round(percentage)}%
        </span>
      </div>
    </div>
  );
};

export const ShopifyOrders: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [orders, setOrders] = useState<ShopifyOrder[]>([]);
  const [filteredOrders, setFilteredOrders] = useState<ShopifyOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<ShopifyOrder | null>(null);
  const [lastSynced, setLastSynced] = useState<string | null>(null);

  const {
    stats: aggregateStats,
    loading: statsLoading,
    refresh: refreshStats,
    lastUpdated: statsLastUpdated,
    isCached: statsIsCached
  } = useOrderStats({
    enabled: !!user,
    autoRefresh: true,
    refreshInterval: 30000,
    maxAge: 60
  });

  const stats: ExtendedStats = useMemo(() => ({
    totalOrders: aggregateStats?.total_orders || 0,
    totalRevenue: aggregateStats?.total_revenue || 0,
    pendingOrders: aggregateStats?.pending_orders || 0,
    fulfilledOrders: aggregateStats?.fulfilled_orders || 0,
    fulfillmentRate: aggregateStats?.fulfillment_rate || 0,
    withTracking: aggregateStats?.with_tracking || 0,
    printedToday: aggregateStats?.printed_today || 0,
    readyToShip: aggregateStats?.ready_to_ship || 0,
    toPrepare: aggregateStats?.to_prepare || 0,
    avgOrderValue: aggregateStats?.avg_order_value || 0,
    todayOrders: aggregateStats?.today_orders || 0,
    todayRevenue: aggregateStats?.today_revenue || 0,
    ordersWithMissingItems: aggregateStats?.orders_with_missing_items || 0,
  }), [aggregateStats]);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(false);
  const [newOrderAlert, setNewOrderAlert] = useState<ShopifyOrder | null>(null);
  const realtimeChannelRef = useRef<RealtimeChannel | null>(null);
  const [lastWebhookReceived, setLastWebhookReceived] = useState<string | null>(null);
  const [showSyncDropdown, setShowSyncDropdown] = useState(false);
  const syncDropdownRef = useRef<HTMLDivElement>(null);

  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [syncProgress, setSyncProgress] = useState<SyncProgress>({
    isActive: false,
    syncedCount: 0,
    totalToSync: 0,
    status: '',
    jobId: null,
    canCancel: false,
  });
  const syncAbortRef = useRef(false);
  const ordersFetchIdRef = useRef(0);
  const lastPageChangeRef = useRef<{ page: number; ts: number }>({ page: 0, ts: 0 });

  const [showSettings, setShowSettings] = useState(false);
  const [togglingReadyId, setTogglingReadyId] = useState<string | null>(null);
  const [fulfillOrderTarget, setFulfillOrderTarget] = useState<ShopifyOrder | null>(null);
  const [fulfilling, setFulfilling] = useState(false);
  const [finishOrderTarget, setFinishOrderTarget] = useState<ShopifyOrder | null>(null);
  const [finishingOrderId, setFinishingOrderId] = useState<number | null>(null);
  const [reprintingOrderId, setReprintingOrderId] = useState<number | null>(null);
  const [printInvoiceTarget, setPrintInvoiceTarget] = useState<ShopifyOrder | null>(null);
  const [fulfillmentSettings, setFulfillmentSettings] = useState<FulfillmentSettings>({
    auto_fulfillment_enabled: false,
    default_carrier: 'PostNL',
    default_shopify_location_id: null,
  });
  const [shopifyLocations, setShopifyLocations] = useState<ShopifyLocation[]>([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState<string>('all');
  const [filtersResetKey, setFiltersResetKey] = useState(0);
  const [activeSearchTerm, setActiveSearchTerm] = useState<string>('');
  const [activeFinancialStatus, setActiveFinancialStatus] = useState<string>('');
  const [activeFulfillmentStatus, setActiveFulfillmentStatus] = useState<string>('');
  const [bulkPickOrders, setBulkPickOrders] = useState<ShopifyOrder[]>([]);
  const [bulkAction, setBulkAction] = useState<{ type: 'mark_ready' | 'finish_orders'; orders: ShopifyOrder[] } | null>(null);
  const [partialRefundWarning, setPartialRefundWarning] = useState<{ order: ShopifyOrder; action: 'ready' | 'finish' } | null>(null);

  const getAuthHeaders = useCallback(async (token?: string): Promise<Record<string, string>> => {
    const headers = await getValidAuthHeaders(token)
    if (!headers) throw new Error('Not authenticated')
    return headers
  }, []);

  const handleAuthError = useCallback(async (
    response: Response, 
    retryFn: (retryCount: number, token?: string) => Promise<void>,
    retryCount: number = 0
  ): Promise<boolean> => {
    // Max 1 retry to prevent infinite loops
    if (retryCount >= 1) {
      return false;
    }

    if (response.status === 401) {
      const newToken = await refreshToken();
      if (newToken) {
        try {
          await retryFn(retryCount + 1, newToken);
          return true;
        } catch {
          return false;
        }
      }
      return false;
    }
    return false;
  }, []);

  const fetchOrders = useCallback(async (
    showLoading = true, 
    page = 1, 
    pageSize = 50,
    filterType?: string,
    searchTerm?: string,
    financialStatus?: string,
    fulfillmentStatus?: string,
    retryCount: number = 0,
    token?: string
  ) => {
    const fetchId = ++ordersFetchIdRef.current;
    if (showLoading) setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        sync: 'false',
      });

      // Add filter and search parameters
      if (filterType && filterType !== 'all') {
        params.append('filterType', filterType);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (financialStatus) {
        params.append('financialStatus', financialStatus);
      }
      if (fulfillmentStatus) {
        params.append('fulfillmentStatus', fulfillmentStatus);
      }

      const headers = await getAuthHeaders(token);
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-fetch-orders?${params}`,
        { headers }
      );

      if (response.status === 401) {
        // Only try to refresh token if we haven't already retried
        if (retryCount === 0) {
          const retried = await handleAuthError(
            response, 
            (newRetryCount, newToken) => fetchOrders(showLoading, page, pageSize, filterType, searchTerm, financialStatus, fulfillmentStatus, newRetryCount, newToken),
            retryCount
          );
          // If retry was successful, we've already updated state, just return
          if (retried) {
            return;
          }
        }
        // If we get here, either retry failed or we've already retried once
        // Only show error if we actually tried and failed
        if (retryCount >= 1 || (retryCount === 0 && !isTokenExpired())) {
          setError('Authentication failed. Please refresh the page and try again.');
          if (showLoading) setLoading(false);
          return;
        }
        // If token is expired and we couldn't refresh, show error
        setError('Session expired. Please refresh the page and log in again.');
        if (showLoading) setLoading(false);
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        if (data.needsConnection) {
          setError('No Shopify store connected. Please connect your store first.');
          setTimeout(() => navigate('/shopify'), 2000);
          return;
        }
        throw new Error(data.error || 'Failed to fetch orders');
      }

      if (fetchId !== ordersFetchIdRef.current) return;

      const result = data.orders || [];
      setOrders(result);
      setFilteredOrders(result);
      if (data.pagination) {
        setPagination(data.pagination);
      }
      refreshStats();
      setLastSynced(new Date().toISOString());
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch orders');
    } finally {
      if (showLoading && fetchId === ordersFetchIdRef.current) setLoading(false);
    }
  }, [getAuthHeaders, refreshStats, navigate, handleAuthError]);

  const runSyncChunk = useCallback(async (jobId: string): Promise<boolean> => {
    if (syncAbortRef.current) {
      return false;
    }

    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-sync-chunk?jobId=${jobId}`,
        { headers, signal: AbortSignal.timeout(55_000) },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.error('[sync] shopify-sync-chunk failed', {
          status: response.status,
          error: data.error,
          jobId,
        });
        throw new Error(data.error || `Sync chunk failed (HTTP ${response.status})`);
      }

      setSyncProgress(prev => ({
        ...prev,
        syncedCount: data.syncedCount,
        totalToSync: data.totalToSync,
        status: data.message,
      }));

      if (data.hasMore && !syncAbortRef.current) {
        await new Promise(resolve => setTimeout(resolve, 100));
        return runSyncChunk(jobId);
      }

      return true;
    } catch (err) {
      if (err instanceof Error && err.name === 'TimeoutError') {
        throw new Error('Sync timed out — try again; progress is saved and will resume.')
      }
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        throw new Error(
          'Sync request failed before a response was received (network or gateway). Check DevTools Network tab for the shopify-sync-chunk request status.',
        );
      }
      throw err
    }
  }, [getAuthHeaders]);

  const syncOrders = useCallback(async (options?: { hours?: number; full?: boolean }) => {
    setSyncing(true);
    setError(null);
    setSuccess(null);
    syncAbortRef.current = false;
    setShowSyncDropdown(false);

    const syncLabel = options?.hours
      ? `Refreshing last ${options.hours}h...`
      : options?.full
        ? 'Starting full sync...'
        : 'Starting incremental sync...';

    setSyncProgress({
      isActive: true,
      syncedCount: 0,
      totalToSync: 0,
      status: syncLabel,
      jobId: null,
      canCancel: true,
    });

    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams();

      if (options?.hours) {
        params.set('hours', options.hours.toString());
      } else if (options?.full) {
        params.set('incremental', 'false');
      } else {
        params.set('incremental', 'true');
      }

      const startResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-start-sync?${params}`,
        { headers }
      );

      const startData = await startResponse.json();

      if (!startResponse.ok) {
        throw new Error(startData.error || 'Failed to start sync');
      }

      setSyncProgress(prev => ({
        ...prev,
        jobId: startData.jobId,
        totalToSync: startData.totalToSync,
        syncedCount: startData.syncedCount || 0,
        status: startData.message,
      }));

      if (startData.status === 'completed') {
        setSuccess('Orders are already up to date');
        await fetchOrders(false);
        setSyncProgress({
          isActive: false,
          syncedCount: 0,
          totalToSync: 0,
          status: '',
          jobId: null,
          canCancel: false,
        });
        setSyncing(false);
        return;
      }

      const success = await runSyncChunk(startData.jobId);

      if (success && !syncAbortRef.current) {
        await fetchOrders(false);
        setSuccess('Orders synced successfully');
        setLastSynced(new Date().toISOString());
      }

      setTimeout(() => {
        setSuccess(null);
        setSyncProgress({
          isActive: false,
          syncedCount: 0,
          totalToSync: 0,
          status: '',
          jobId: null,
          canCancel: false,
        });
      }, 2000);
    } catch (err) {
      console.error('Error syncing orders:', err);
      setError(err instanceof Error ? err.message : 'Failed to sync orders');
      setSyncProgress({
        isActive: false,
        syncedCount: 0,
        totalToSync: 0,
        status: '',
        jobId: null,
        canCancel: false,
      });
    } finally {
      setSyncing(false);
    }
  }, [getAuthHeaders, runSyncChunk, fetchOrders]);

  const quickRefresh = useCallback(() => syncOrders({ hours: 24 }), [syncOrders]);
  const fullSync = useCallback(() => syncOrders({ full: true }), [syncOrders]);

  const cancelSync = useCallback(async () => {
    syncAbortRef.current = true;
    if (syncProgress.jobId) {
      try {
        await supabase
          .from('sync_jobs')
          .update({ status: 'cancelled', updated_at: new Date().toISOString() })
          .eq('id', syncProgress.jobId);
      } catch (err) {
        console.error('Error cancelling sync:', err);
      }
    }
    setSyncing(false);
    setSyncProgress({
      isActive: false,
      syncedCount: 0,
      totalToSync: 0,
      status: '',
      jobId: null,
      canCancel: false,
    });
    setSuccess('Sync cancelled');
    setTimeout(() => setSuccess(null), 3000);
  }, [syncProgress.jobId]);

  const checkActiveSync = useCallback(async () => {
    if (!user) return;

    const adminId = getAdminId(user);

    const { data } = await supabase
      .rpc('get_active_sync_job', { p_admin_id: adminId, p_job_type: 'orders' });

    if (data && data.length > 0) {
      const job = data[0];
      setSyncProgress({
        isActive: true,
        syncedCount: job.synced_count,
        totalToSync: job.total_to_sync,
        status: 'Resuming sync...',
        jobId: job.id,
        canCancel: true,
      });
      setSyncing(true);
      syncAbortRef.current = false;
      runSyncChunk(job.id).then(async (success) => {
        if (success) {
          await fetchOrders(false);
          setSuccess('Orders synced successfully');
          setLastSynced(new Date().toISOString());
        }
        setTimeout(() => {
          setSuccess(null);
          setSyncProgress({
            isActive: false,
            syncedCount: 0,
            totalToSync: 0,
            status: '',
            jobId: null,
            canCancel: false,
          });
        }, 2000);
        setSyncing(false);
      }).catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to resume sync');
        setSyncProgress({
          isActive: false,
          syncedCount: 0,
          totalToSync: 0,
          status: '',
          jobId: null,
          canCancel: false,
        });
        setSyncing(false);
      });
    }
  }, [user, runSyncChunk, fetchOrders]);

  const handlePageChange = useCallback((newPage: number) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    const now = Date.now();
    const last = lastPageChangeRef.current;
    if (last.page === newPage && now - last.ts < 400) return;
    lastPageChangeRef.current = { page: newPage, ts: now };
    fetchOrders(false, newPage, pagination.pageSize, activeQuickFilter, activeSearchTerm, activeFinancialStatus, activeFulfillmentStatus);
  }, [fetchOrders, pagination.totalPages, pagination.pageSize, activeQuickFilter, activeSearchTerm, activeFinancialStatus, activeFulfillmentStatus]);

  const fetchFulfillmentSettings = useCallback(async () => {
    if (!user) return;

    const adminId = getAdminId(user);

    const { data } = await supabase
      .from('shopify_settings')
      .select('auto_fulfillment_enabled, default_carrier, default_shopify_location_id, last_webhook_received_at')
      .eq('admin_id', adminId)
      .maybeSingle();

    if (data) {
      setFulfillmentSettings({
        auto_fulfillment_enabled: data.auto_fulfillment_enabled ?? false,
        default_carrier: data.default_carrier ?? 'PostNL',
        default_shopify_location_id: data.default_shopify_location_id,
      });
      setLastWebhookReceived(data.last_webhook_received_at);
    }

    const { data: storeData } = await supabase
      .from('shopify_stores')
      .select('default_location_id, shop_domain')
      .eq('admin_id', adminId)
      .maybeSingle();

    if (storeData?.default_location_id) {
      setShopifyLocations([
        { id: storeData.default_location_id, name: 'Primary Location' }
      ]);
    }
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (syncDropdownRef.current && !syncDropdownRef.current.contains(event.target as Node)) {
        setShowSyncDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const webhookHealthy = useMemo(() => {
    if (!lastWebhookReceived) return null;
    const lastReceived = new Date(lastWebhookReceived);
    const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return lastReceived > hourAgo;
  }, [lastWebhookReceived]);

  const saveFulfillmentSettings = useCallback(async (newSettings: Partial<FulfillmentSettings>) => {
    if (!user) return;

    const adminId = getAdminId(user);

    setSavingSettings(true);
    try {
      const { error: updateError } = await supabase
        .from('shopify_settings')
        .upsert({
          admin_id: adminId,
          ...fulfillmentSettings,
          ...newSettings,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'admin_id' });

      if (updateError) throw updateError;

      setFulfillmentSettings(prev => ({ ...prev, ...newSettings }));
      setSuccess(newSettings.auto_fulfillment_enabled !== undefined
        ? (newSettings.auto_fulfillment_enabled ? 'Auto-fulfillment enabled' : 'Auto-fulfillment disabled')
        : 'Settings saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  }, [user, fulfillmentSettings]);

  useEffect(() => {
    fetchOrders();
    fetchFulfillmentSettings();
    checkActiveSync();
  }, [fetchOrders, fetchFulfillmentSettings, checkActiveSync]);

  // Read the latest selectedOrder / refreshStats inside the realtime handlers
  // without listing them as effect deps (which would tear down and resubscribe
  // the channel on every order change, dropping events during the gap).
  const selectedOrderRef = useRef(selectedOrder);
  const refreshStatsRef = useRef(refreshStats);
  useEffect(() => { selectedOrderRef.current = selectedOrder; }, [selectedOrder]);
  useEffect(() => { refreshStatsRef.current = refreshStats; }, [refreshStats]);

  const realtimeAdminId = getAdminId(user);

  useEffect(() => {
    if (!realtimeAdminId) return;

    const adminId = realtimeAdminId;

    const channel = supabase
      .channel('shopify-orders-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'shopify_orders',
          filter: `admin_id=eq.${adminId}`,
        },
        (payload) => {
          const newOrder = payload.new as ShopifyOrder;
          setOrders(prev => {
            const exists = prev.some(o => o.id === newOrder.id);
            if (exists) return prev;
            return [newOrder, ...prev];
          });
          setFilteredOrders(prev => {
            const exists = prev.some(o => o.id === newOrder.id);
            if (exists) return prev;
            return [newOrder, ...prev];
          });
          setNewOrderAlert(newOrder);
          setTimeout(() => setNewOrderAlert(null), 5000);
          refreshStatsRef.current(true);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'shopify_orders',
          filter: `admin_id=eq.${adminId}`,
        },
        (payload) => {
          const updatedOrder = payload.new as ShopifyOrder;
          setOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
          setFilteredOrders(prev => prev.map(o => o.id === updatedOrder.id ? updatedOrder : o));
          if (selectedOrderRef.current?.id === updatedOrder.id) {
            setSelectedOrder(updatedOrder);
          }
        }
      )
      .subscribe((status) => {
        setIsRealtimeConnected(status === 'SUBSCRIBED');
      });

    realtimeChannelRef.current = channel;

    return () => {
      if (realtimeChannelRef.current) {
        supabase.removeChannel(realtimeChannelRef.current);
      }
    };
  }, [realtimeAdminId]);

  const applyQuickFilter = useCallback((filterType: string) => {
    setActiveQuickFilter(filterType);
    // Reset to page 1 when filter changes
    // If filter is 'all', clear other filters too and remount OrderFilters so search/status UI resets
    if (filterType === 'all') {
      setActiveSearchTerm('');
      setActiveFinancialStatus('');
      setActiveFulfillmentStatus('');
      setFiltersResetKey((k) => k + 1);
      fetchOrders(true, 1, 50, 'all', '', '', '');
    } else {
      // Fetch orders from database with the filter applied
      fetchOrders(true, 1, 50, filterType, activeSearchTerm, activeFinancialStatus, activeFulfillmentStatus);
    }
  }, [fetchOrders, activeSearchTerm, activeFinancialStatus, activeFulfillmentStatus]);

  const handleSearchAndFilterChange = useCallback((
    search: string,
    financialStatus: string,
    fulfillmentStatus: string
  ) => {
    setActiveSearchTerm(search);
    setActiveFinancialStatus(financialStatus);
    setActiveFulfillmentStatus(fulfillmentStatus);
    // Reset to page 1 and fetch from database. Use showLoading: false so the search bar
    // and layout stay visible; only the orders list updates when the fetch completes.
    fetchOrders(false, 1, 50, activeQuickFilter, search, financialStatus, fulfillmentStatus);
  }, [fetchOrders, activeQuickFilter]);

  const handleFilterChange = useCallback((filtered: ShopifyOrder[]) => {
    // This is called from OrderFilters when using advanced filters (date, country, amount)
    // Keep client-side filtering for these advanced filters
    // Don't reset activeQuickFilter - preserve it so quick filters can work with advanced filters
    setFilteredOrders(filtered);
    // Only reset quick filter if we're explicitly clearing all filters
    // Don't reset it when OrderFilters applies its filters on top of quick filter results
  }, []);

  const handleOrderClick = (order: ShopifyOrder) => {
    setSelectedOrder(order);
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
  };

  const executeToggleReady = async (orderId: string, currentReady: boolean) => {
    if (!user) return;

    setTogglingReadyId(orderId);
    try {
      const newReady = !currentReady;
      const adminId = getAdminId(user);

      const { data, error: rpcError } = await supabase.rpc('update_shopify_order_ready_status', {
        p_order_id: orderId,
        p_admin_id: adminId,
        p_is_ready: newReady,
        p_user_id: user.id,
      });

      if (rpcError) throw rpcError;

      const result = data as { success: boolean; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to update ready status');
      }

      setOrders(prev => prev.map(o =>
        o.id === orderId
          ? { ...o, is_ready: newReady, ready_at: newReady ? new Date().toISOString() : null, ready_by: newReady ? user.id : null }
          : o
      ));
      setFilteredOrders(prev => prev.map(o =>
        o.id === orderId
          ? { ...o, is_ready: newReady, ready_at: newReady ? new Date().toISOString() : null, ready_by: newReady ? user.id : null }
          : o
      ));
    } catch (err) {
      console.error('Error toggling ready status:', err);
      setError(err instanceof Error ? err.message : 'Failed to update ready status');
    } finally {
      setTogglingReadyId(null);
    }
  };

  const handleToggleReady = async (orderId: string, currentReady: boolean) => {
    if (!user) return;
    const order = orders.find(o => o.id === orderId) || filteredOrders.find(o => o.id === orderId);
    if (order && order.financial_status === 'PARTIALLY_REFUNDED' && !currentReady) {
      setPartialRefundWarning({ order, action: 'ready' });
      return;
    }
    executeToggleReady(orderId, currentReady);
  };

  const handleFulfillClick = (order: ShopifyOrder) => {
    setFulfillOrderTarget(order);
  };

  const handleFulfillSubmit = async (trackingCode: string, carrier: string, notifyCustomer: boolean) => {
    if (!fulfillOrderTarget) return;

    setFulfilling(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-fulfill-order`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            orderId: fulfillOrderTarget.shopify_order_id,
            trackingCode,
            carrier,
            notifyCustomer,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fulfill order');
      }

      setSuccess(data.message || `Order ${fulfillOrderTarget.order_number} fulfilled successfully`);
      setTimeout(() => setSuccess(null), 5000);
      setFulfillOrderTarget(null);
      fetchOrders(false);
    } finally {
      setFulfilling(false);
    }
  };

  const handleFinishClick = (order: ShopifyOrder) => {
    if (order.fulfillment_status === 'FULFILLED' || order.fulfilled_at) {
      setError('This order has already been finished and cannot be processed again.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    if (order.financial_status === 'PARTIALLY_REFUNDED') {
      setPartialRefundWarning({ order, action: 'finish' });
      return;
    }
    setFinishOrderTarget(order);
  };

  const handlePartialRefundConfirm = () => {
    if (!partialRefundWarning) return;
    const { order, action } = partialRefundWarning;
    setPartialRefundWarning(null);
    if (action === 'ready') {
      executeToggleReady(order.id, order.is_ready);
    } else {
      setFinishOrderTarget(order);
    }
  };

  const handleFinishOrder = async (orderId: number): Promise<FinishOrderResponse> => {
    setFinishingOrderId(orderId);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-finish-order`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ shopifyOrderId: orderId }),
        }
      );

      const text = await response.text();
      let data: FinishOrderResponse | null = null;
      try {
        data = text ? (JSON.parse(text) as FinishOrderResponse) : null;
      } catch {
        if (!response.ok) {
          const msg = response.status === 401 ? 'Authentication failed. Please log in again.' : 'Request failed. Please try again or contact support.';
          return { success: false, error: msg };
        }
      }

      if (!response.ok) {
        const defaultMessage = response.status === 401
          ? 'Authentication failed. Please log in again.'
          : 'Request failed. Please try again or contact support.';
        const errorMessage = (data && typeof data.error === 'string') ? data.error : defaultMessage;
        return { success: false, error: errorMessage };
      }

      if (data?.success) {
        fetchOrders(false);
        refreshStats(true);
      }

      return data ?? { success: false, error: 'Failed to finish order' };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to finish order',
      };
    } finally {
      setFinishingOrderId(null);
    }
  };

  const handleReprintClick = async (order: ShopifyOrder) => {
    setReprintingOrderId(order.shopify_order_id);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-reprint-label`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ shopifyOrderId: order.shopify_order_id }),
        }
      );

      const data: ReprintLabelResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to reprint label');
      }

      if (data.labelPdf) {
        const blob = new Blob(
          [Uint8Array.from(atob(data.labelPdf), c => c.charCodeAt(0))],
          { type: 'application/pdf' }
        );
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        setSuccess(`Label reprinted for order ${order.order_number} (print #${data.printCount})`);
        setTimeout(() => setSuccess(null), 5000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reprint label');
    } finally {
      setReprintingOrderId(null);
    }
  };

  const handlePrintInvoiceClick = (order: ShopifyOrder) => {
    setPrintInvoiceTarget(order);
  };

  const handleBulkPrintPick = useCallback((selectedOrderIds: string[]) => {
    const selectedOrders = orders.filter(o => selectedOrderIds.includes(o.id));
    if (selectedOrders.length > 0) {
      setBulkPickOrders(selectedOrders);
    }
  }, [orders]);

  const handleBulkMarkReady = useCallback((selectedOrderIds: string[]) => {
    const selected = orders.filter(o => selectedOrderIds.includes(o.id));
    if (selected.length > 0) {
      setBulkAction({ type: 'mark_ready', orders: selected });
    }
  }, [orders]);

  const handleBulkFinish = useCallback((selectedOrderIds: string[]) => {
    const selected = orders.filter(o => selectedOrderIds.includes(o.id));
    if (selected.length > 0) {
      setBulkAction({ type: 'finish_orders', orders: selected });
    }
  }, [orders]);

  const handleBulkComplete = useCallback(() => {
    setBulkAction(null);
    fetchOrders(false);
    refreshStats(true);
  }, [fetchOrders, refreshStats]);

  const quickFilters = useMemo(() => [
    { id: 'all', label: 'All Orders', count: stats.totalOrders, icon: Package },
    { id: 'to_prepare', label: 'To Prepare', count: stats.toPrepare, icon: ClipboardList },
    { id: 'ready', label: 'Ready to Ship', count: stats.readyToShip, icon: PackageCheck },
    { id: 'pending', label: 'Pending Payment', count: stats.pendingOrders, icon: Timer },
    { id: 'fulfilled', label: 'Fulfilled', count: stats.fulfilledOrders, icon: Truck },
    { id: 'today', label: "Today's Orders", count: stats.todayOrders, icon: Calendar },
    { id: 'missing_items', label: 'Orders with Missing Items', count: stats.ordersWithMissingItems, icon: AlertTriangle },
  ], [stats]);

  const primaryCurrency = aggregateStats?.primary_currency || 'EUR';

  if (loading) {
    return (
      <div className="p-4 lg:p-6 xl:p-8 w-full">
        <div className="mb-8">
          <div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-2" />
          <div className="h-5 w-64 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mb-8">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse" />
          ))}
        </div>
        <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse mb-6" />
        <div className="h-[600px] bg-gray-200 dark:bg-gray-700 rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 xl:p-8 w-full">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Shopify Orders
            </h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Manage and fulfill your Shopify orders
                {lastSynced && (
                  <span className="ml-2">
                    - Last synced {new Date(lastSynced).toLocaleTimeString()}
                  </span>
                )}
              </p>
              <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                isRealtimeConnected
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
              }`}>
                {isRealtimeConnected ? (
                  <>
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                    </span>
                    Live
                  </>
                ) : (
                  <>
                    <WifiOff className="h-3 w-3" />
                    Offline
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {webhookHealthy === false && (
              <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>Webhooks may be delayed</span>
              </div>
            )}
            <Button
              onClick={quickRefresh}
              loading={syncing}
              disabled={syncing}
              variant="secondary"
              className="flex items-center gap-2"
              title="Refresh orders from the last 24 hours"
            >
              <History className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Last 24h</span>
            </Button>
            <div className="relative" ref={syncDropdownRef}>
              <Button
                onClick={() => setShowSyncDropdown(!showSyncDropdown)}
                loading={syncing}
                disabled={syncing}
                variant="primary"
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Sync</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
              {showSyncDropdown && !syncing && (
                <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl z-50 overflow-hidden">
                  <div className="p-1">
                    <button
                      onClick={() => syncOrders()}
                      className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <RefreshCw className="h-5 w-5 text-cyan-600 dark:text-cyan-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">Sync New Orders</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Orders since last sync</p>
                      </div>
                    </button>
                    <button
                      onClick={quickRefresh}
                      className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                    >
                      <History className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">Quick Refresh</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Last 24 hours of orders</p>
                      </div>
                    </button>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                    <button
                      onClick={fullSync}
                      className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left"
                    >
                      <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-gray-100">Full Sync</p>
                        <p className="text-xs text-amber-600 dark:text-amber-400">All orders - may take a while</p>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3">
          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <p className="text-red-700 dark:text-red-300 flex-1 text-sm">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 dark:text-red-400 hover:text-red-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
          <p className="text-green-700 dark:text-green-300 flex-1 text-sm">{success}</p>
          <button onClick={() => setSuccess(null)} className="text-green-600 dark:text-green-400 hover:text-green-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {newOrderAlert && (
        <div className="mb-6 p-4 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-xl flex items-center gap-3 animate-pulse">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white flex-shrink-0">
            <ShoppingCart className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <p className="text-cyan-800 dark:text-cyan-200 font-semibold">New Order Received</p>
            <p className="text-cyan-700 dark:text-cyan-300 text-sm">
              {newOrderAlert.order_number}
              {newOrderAlert.customer_name && ` from ${newOrderAlert.customer_name}`}
              {' - '}
              {formatCurrency(newOrderAlert.total_price, newOrderAlert.currency)}
            </p>
          </div>
          <button
            onClick={() => {
              setSelectedOrder(newOrderAlert);
              setNewOrderAlert(null);
            }}
            className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            View
          </button>
          <button onClick={() => setNewOrderAlert(null)} className="text-cyan-600 dark:text-cyan-400 hover:text-cyan-700">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
            Enterprise Statistics
          </span>
          {statsLoading && (
            <Loader2 className="h-3 w-3 animate-spin text-cyan-500" />
          )}
          {!statsLoading && statsLastUpdated && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Updated {statsLastUpdated.toLocaleTimeString()}
            </span>
          )}
          {statsIsCached && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded">
              CACHED
            </span>
          )}
        </div>
        <button
          onClick={() => refreshStats(true)}
          disabled={statsLoading}
          className="text-xs text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 font-medium flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${statsLoading ? 'animate-spin' : ''}`} />
          Refresh Stats
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 lg:gap-4 mb-6">
        <StatCard
          icon={<ShoppingCart className="h-5 w-5 text-white" />}
          label="Total Orders"
          value={formatStatNumber(stats.totalOrders)}
          subValue={`${stats.todayOrders} today`}
          gradient="from-blue-500 to-cyan-500"
        />
        <StatCard
          icon={<DollarSign className="h-5 w-5 text-white" />}
          label="Total Revenue"
          value={formatCurrency(stats.totalRevenue, primaryCurrency)}
          subValue={`Avg ${formatCurrency(stats.avgOrderValue, primaryCurrency)}`}
          gradient="from-green-500 to-emerald-500"
        />
        <StatCard
          icon={<ClipboardList className="h-5 w-5 text-white" />}
          label="To Prepare"
          value={formatStatNumber(stats.toPrepare)}
          subValue="Need picking"
          gradient="from-blue-600 to-sky-500"
          onClick={() => applyQuickFilter('to_prepare')}
        />
        <StatCard
          icon={<PackageCheck className="h-5 w-5 text-white" />}
          label="Ready to Ship"
          value={formatStatNumber(stats.readyToShip)}
          subValue="Awaiting fulfillment"
          gradient="from-amber-500 to-orange-500"
          onClick={() => applyQuickFilter('ready')}
        />
        <div className="relative overflow-hidden rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-5 col-span-2 md:col-span-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Fulfillment Rate</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {formatStatNumber(stats.fulfilledOrders)}/{formatStatNumber(stats.totalOrders)}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">orders fulfilled</p>
            </div>
            <ProgressRing percentage={stats.fulfillmentRate} />
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {quickFilters.map(filter => {
          const Icon = filter.icon;
          return (
            <button
              key={filter.id}
              onClick={() => applyQuickFilter(filter.id)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                activeQuickFilter === filter.id
                  ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/25'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-cyan-300 dark:hover:border-cyan-700'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="hidden sm:inline">{filter.label}</span>
              <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                activeQuickFilter === filter.id
                  ? 'bg-white/20 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
              }`}>
                {filter.count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="card mb-6">
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="w-full p-4 flex items-center justify-between text-left"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center">
              <Zap className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                Auto-Fulfillment Settings
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {fulfillmentSettings.auto_fulfillment_enabled ? (
                  <span className="text-green-600 dark:text-green-400 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Enabled - Orders auto-fulfill after print
                  </span>
                ) : (
                  <span className="text-gray-500">Disabled - Manual fulfillment mode</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              fulfillmentSettings.auto_fulfillment_enabled
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            }`}>
              {fulfillmentSettings.auto_fulfillment_enabled ? 'ON' : 'OFF'}
            </span>
            {showSettings ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </div>
        </button>

        {showSettings && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Zap className="h-4 w-4" />
                  Auto-Fulfillment Mode
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  When enabled, orders automatically fulfill in Shopify after invoice print
                </p>
                <button
                  onClick={() => saveFulfillmentSettings({
                    auto_fulfillment_enabled: !fulfillmentSettings.auto_fulfillment_enabled
                  })}
                  disabled={savingSettings}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    fulfillmentSettings.auto_fulfillment_enabled
                      ? 'bg-green-500'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${
                      fulfillmentSettings.auto_fulfillment_enabled ? 'translate-x-7' : 'translate-x-0.5'
                    }`}
                  />
                  {savingSettings && (
                    <Loader2 className="absolute inset-0 m-auto h-4 w-4 animate-spin text-white" />
                  )}
                </button>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <Truck className="h-4 w-4" />
                  Default Carrier
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Shipping carrier used for fulfillment notifications
                </p>
                <select
                  value={fulfillmentSettings.default_carrier}
                  onChange={(e) => saveFulfillmentSettings({ default_carrier: e.target.value })}
                  disabled={savingSettings}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  {CARRIERS.map(carrier => (
                    <option key={carrier.value} value={carrier.value}>
                      {carrier.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <MapPin className="h-4 w-4" />
                  Shopify Location
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Fulfillment location for Shopify orders
                </p>
                <select
                  value={fulfillmentSettings.default_shopify_location_id ?? ''}
                  onChange={(e) => saveFulfillmentSettings({
                    default_shopify_location_id: e.target.value ? Number(e.target.value) : null
                  })}
                  disabled={savingSettings || shopifyLocations.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent disabled:opacity-50"
                >
                  <option value="">Select location</option>
                  {shopifyLocations.map(location => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                How Auto-Fulfillment Works
              </h4>
              <ul className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                <li>1. Select an unfulfilled order and print the invoice</li>
                <li>2. Enter the tracking code when prompted</li>
                <li>3. The order is automatically marked as fulfilled in Shopify</li>
                <li>4. Customer receives shipping notification with tracking info</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <OrderFilters
        key={filtersResetKey}
        orders={orders}
        onFilterChange={handleFilterChange}
        onSearchAndFilterChange={handleSearchAndFilterChange}
      />

      <OrdersList
        orders={filteredOrders}
        onOrderClick={handleOrderClick}
        onToggleReady={handleToggleReady}
        onFulfillClick={handleFulfillClick}
        onFinishClick={handleFinishClick}
        onReprintClick={handleReprintClick}
        onPrintInvoiceClick={handlePrintInvoiceClick}
        onBulkPrintPick={handleBulkPrintPick}
        onBulkMarkReady={handleBulkMarkReady}
        onBulkFinish={handleBulkFinish}
        togglingReadyId={togglingReadyId}
        finishingOrderId={finishingOrderId}
        reprintingOrderId={reprintingOrderId}
      />

      {pagination.totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.total)} of {pagination.total.toLocaleString()} orders
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handlePageChange(1)}
              disabled={!pagination.hasPrevPage}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              First
            </button>
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={!pagination.hasPrevPage}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Previous
            </button>

            {(() => {
              const pages: (number | string)[] = [];
              const current = pagination.page;
              const total = pagination.totalPages;

              if (total <= 7) {
                for (let i = 1; i <= total; i++) pages.push(i);
              } else {
                pages.push(1);
                if (current > 3) pages.push('...');
                for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
                  if (!pages.includes(i)) pages.push(i);
                }
                if (current < total - 2) pages.push('...');
                if (!pages.includes(total)) pages.push(total);
              }

              return pages.map((p, idx) => (
                typeof p === 'number' ? (
                  <button
                    key={idx}
                    onClick={() => handlePageChange(p)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                      p === current
                        ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/25'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {p}
                  </button>
                ) : (
                  <span key={idx} className="px-2 text-gray-400">...</span>
                )
              ));
            })()}

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={!pagination.hasNextPage}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Next
            </button>
            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={!pagination.hasNextPage}
              className="px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Last
            </button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Go to:</span>
            <input
              key={pagination.page}
              type="number"
              min={1}
              max={pagination.totalPages}
              defaultValue={pagination.page}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                e.stopPropagation();
                if (e.repeat) return;
                const value = parseInt((e.target as HTMLInputElement).value);
                if (!Number.isNaN(value) && value >= 1 && value <= pagination.totalPages) {
                  handlePageChange(value);
                }
              }}
              className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>
        </div>
      )}

      {syncProgress.isActive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-2xl flex items-center justify-center">
                <RefreshCw className="h-8 w-8 text-white animate-spin" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Syncing Orders
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {syncProgress.status}
              </p>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-4 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: syncProgress.totalToSync > 0
                      ? `${Math.min((syncProgress.syncedCount / syncProgress.totalToSync) * 100, 100)}%`
                      : '10%'
                  }}
                />
              </div>
              <div className="flex items-center justify-center gap-4 mb-4">
                <p className="text-sm font-medium text-cyan-600 dark:text-cyan-400">
                  {syncProgress.syncedCount.toLocaleString()}
                  {syncProgress.totalToSync > 0 && (
                    <span className="text-gray-500 dark:text-gray-400">
                      {' / '}{syncProgress.totalToSync.toLocaleString()}
                    </span>
                  )}
                  {' orders'}
                </p>
                {syncProgress.totalToSync > 0 && (
                  <span className="text-sm font-bold text-gray-900 dark:text-gray-100">
                    {Math.round((syncProgress.syncedCount / syncProgress.totalToSync) * 100)}%
                  </span>
                )}
              </div>
              {syncProgress.canCancel && (
                <button
                  onClick={cancelSync}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
                >
                  Cancel
                </button>
              )}
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-3">
                You can navigate away - sync will continue in background
              </p>
            </div>
          </div>
        </div>
      )}

      {selectedOrder && (
        <OrderDetails
          order={selectedOrder}
          onClose={handleCloseModal}
          onOrderUpdated={() => fetchOrders(false)}
        />
      )}

      {fulfillOrderTarget && (
        <FulfillmentModal
          order={fulfillOrderTarget}
          carriers={CARRIERS}
          defaultCarrier={fulfillmentSettings.default_carrier}
          onClose={() => setFulfillOrderTarget(null)}
          onSubmit={handleFulfillSubmit}
        />
      )}

      {finishOrderTarget && (
        <FinishOrderModal
          order={finishOrderTarget}
          onClose={() => {
            setFinishOrderTarget(null);
            fetchOrders(false);
            refreshStats(true);
          }}
          onFinish={handleFinishOrder}
          adminId={getAdminId(user)}
        />
      )}

      {printInvoiceTarget && (
        <OrderInvoice
          order={printInvoiceTarget}
          onClose={() => setPrintInvoiceTarget(null)}
          onFulfilled={() => fetchOrders(false)}
          autoFulfillmentEnabled={fulfillmentSettings.auto_fulfillment_enabled}
          defaultCarrier={fulfillmentSettings.default_carrier}
        />
      )}

      {bulkPickOrders.length > 0 && (
        <BulkPickListPrintable
          orders={bulkPickOrders}
          onClose={() => setBulkPickOrders([])}
        />
      )}

      {bulkAction && user && (
        <BulkOrderActionsModal
          orders={bulkAction.orders}
          actionType={bulkAction.type}
          onClose={() => setBulkAction(null)}
          onComplete={handleBulkComplete}
          getAuthHeaders={getAuthHeaders}
          adminId={getAdminId(user)}
          userId={user.id}
        />
      )}

      {partialRefundWarning && (
        <PartialRefundWarningModal
          order={partialRefundWarning.order}
          actionType={partialRefundWarning.action}
          onConfirm={handlePartialRefundConfirm}
          onCancel={() => setPartialRefundWarning(null)}
        />
      )}
    </div>
  );
};

export default ShopifyOrders;
